/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { DateTime } from 'luxon';
// import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import useStore from '@/store/store';
import OrderController from '@/services/OrderController';
import ShopController from '@/services/ShopController';
import DeliveryController from '@/services/DeliveryController';
import Table from '@/ui/table/Table';
import ButtonSecondary from '@/ui/button-secondary/ButtonSecondary';
import DropdownMultiple from '@/ui/dropdown-multiple/DropdownMultiple';
import OutsideClickHandler from '@/shared/OutsideClickHandler';
import { DELIVERY_URL, SHOP_URL } from '@/services/urls';
// import Modal from '@/ui/modal/Modal';
// import RangePicker from '@/ui/range-picker/RangePicker';
import { exportToExcel } from '@/utils/exportToExcel';
import Button from '@/ui/button/Button';
import { MyDateTimePicker } from '@/ui/MyDateTimePicker';
import { lazyWithSuspense } from '@/shared/lazyWithSuspense';
import Order from '@/components/orders/order/Order';
import { canSeeShopFees } from '@/utils/funcs';
import { PAYMENT_TYPES } from '@/utils/constants';
import { PlatformLogo } from '@/ui/Platform-Logo';
import { getCustomStartOfDay } from '@/utils/getCustomStartOfDay';
import { Pocket } from '@/ui/Pocket';
import { useCursorPagination } from '@/utils/useCursorPagination';
import { OrderSummary } from './OrderSummary';
import s from './order-list.module.css';

// import DayView from './day-view/DayView';

// const DayView = lazy(() => import('./day-view/DayView'));
const DayView = lazyWithSuspense(() => import('./day-view/DayView'), null);
// const Order = lazyWithSuspense(() => import('@/components/orders/order/Order'), null);

const PAGE_SIZE = 200;

const VIEW_STATES = {
	NORMAL: 1,
	ORDER_RANGE: 2,
};

const selectedShop = {
	id: -3,
	label: 'Καταστήματα',
	value: 'all',
};

const selectedDelivery = {
	id: -3,
	label: 'Διανομείς',
	value: 'all',
};

const maxDate = DateTime.local().startOf('day').plus({ days: 1, hours: 5 });

// One order (terse backend fields) -> one table/Excel row. Module scope so the
// Excel export can map the full fetched range through the same shape.
const mapOrderRow = (it, index, isMasterOrAdmin) => ({
	id: it.id,
	// createdAtTemp: it.createdAt,
	// originalOrderCreatedAtTemp: it.originalOrderCreatedAt,
	// tempDate: DateTime.fromISO(it.originalOrderCreatedAt).toFormat('dd/HH:mm'),
	index: index + 1,
	// day the order was placed, for the Excel export
	createdAtDate: (() => {
		const dt = DateTime.fromISO(it.originalOrderCreatedAt || it.createdAt);
		return dt.isValid ? dt.toFormat('dd/MM/yyyy') : '';
	})(),
	thirdTime: `${it?.tt === null || it?.tt === undefined ? '' : `${it.tt}'`}`,
	price: it.pr,
	comments: it?.c || '',
	deliveryName: it.d?.name || '',
	shopName: isMasterOrAdmin ? `${it?.sh?.name || '-'}` : undefined,
	clientName: `${it.cl?.f || ''}${it.cl?.p || it.cPh ? ` (${it.cl?.p || it.cPh})` : ''}`,
	address: it.a?.s || it?.area,
	area: it?.area,
	fee: it.fee,
	shopFee: it.shopFee,
	// Piece work (envelopes, newspapers): pieces delivered.
	pieces: it.pcs ?? '',
	distanceMeters: it.distanceMeters ? (it.distanceMeters / 1000).toFixed(1) : '-',
	// same unit as the driven distance above, so the two columns compare
	radiusMeters: it.radiusMeters ? (it.radiusMeters / 1000).toFixed(1) : '-',
	platform: it.platform,
	isCard: it.paymentType === PAYMENT_TYPES.CARD ? 'Ναι' : '',
	woltOrderReferenceId: it.woltOrderReferenceId,
	platformHashOrderId: it.platformHashOrderId,
	platformOrderId: it.platformOrderId,
});

// Takes a deleted order out of the loaded pages and out of the server
// aggregates, so the list needs no refetch and keeps its scroll position.
const removeOrderLocally = ({ items, meta }, orderId) => {
	const order = items?.find(o => o.id === orderId);
	if (!order) {
		return { items, meta };
	}
	const nextItems = items.filter(o => o.id !== orderId);
	if (!meta) {
		return { items: nextItems, meta };
	}
	const nextMeta = { ...meta };
	if (typeof nextMeta.totalCount === 'number') {
		nextMeta.totalCount--;
	}
	if (nextMeta.aggregates?.totals) {
		const t = { ...nextMeta.aggregates.totals };
		t.count--;
		t.price -= order.pr || 0;
		const cardPrice = order.paymentType === PAYMENT_TYPES.CARD ? order.pr || 0 : 0;
		if (t.cardPrice !== undefined) {
			t.cardPrice -= cardPrice;
		}
		t.fee -= order.fee || 0;
		t.shopFee -= order.shopFee || 0;
		t.pieces = (t.pieces || 0) - (order.pcs || 0);
		t.distance -= order.distanceMeters || 0;
		if (order.tt !== null && order.tt !== undefined) {
			t.timeSum -= order.tt;
			t.timeCount--;
		}
		const dropFrom = (rows, id) =>
			rows
				?.map(r =>
					r.id !== id
						? r
						: {
								...r,
								count: r.count - 1,
								price: r.price - (order.pr || 0),
								fee: r.fee - (order.fee || 0),
								shopFee: r.shopFee - (order.shopFee || 0),
							},
				)
				.filter(r => r.count > 0);
		// The pair rows are what the summary panel actually counts, so the deleted
		// order has to come off its own (shop, driver) bucket as well.
		const dropPair = pairs =>
			pairs
				?.map(p => {
					if (p[0] !== (order.sId ?? null) || p[1] !== (order.dId ?? null)) {
						return p;
					}
					const next = [...p];
					next[2]--;
					next[3] -= order.pr || 0;
					next[4] -= order.fee || 0;
					next[5] -= order.shopFee || 0;
					next[6] -= order.distanceMeters || 0;
					if (order.tt !== null && order.tt !== undefined) {
						next[7] -= order.tt;
						next[8]--;
					}
					if (next.length > 9) {
						next[9] -= order.pcs || 0;
					}
					if (next.length > 10) {
						next[10] -= cardPrice;
					}
					return next;
				})
				.filter(p => p[2] > 0);
		nextMeta.aggregates = {
			totals: t,
			shops: dropFrom(nextMeta.aggregates.shops, order.sId),
			deliveries: dropFrom(nextMeta.aggregates.deliveries, order.dId),
			pairs: dropPair(nextMeta.aggregates.pairs),
		};
	}
	return { items: nextItems, meta: nextMeta };
};

// An admin corrected the pieces of a piece-work job: the order's pieces and
// charge change, and the charge difference moves through the aggregates the
// same way removeOrderLocally takes a whole order out.
const changePiecesLocally = ({ items, meta }, orderId, pieces, shopFee) => {
	const order = items?.find(o => o.id === orderId);
	if (!order) {
		return { items, meta };
	}
	const dFee = (shopFee || 0) - (order.shopFee || 0);
	const dPieces = (pieces || 0) - (order.pcs || 0);
	const nextItems = items.map(o => (o.id === orderId ? { ...o, pcs: pieces, shopFee } : o));
	if (!meta?.aggregates?.totals) {
		return { items: nextItems, meta };
	}
	const t = { ...meta.aggregates.totals };
	t.shopFee = (t.shopFee || 0) + dFee;
	t.pieces = (t.pieces || 0) + dPieces;
	const adjust = (rows, id) =>
		rows?.map(r => (r.id !== id ? r : { ...r, shopFee: (r.shopFee || 0) + dFee }));
	const pairs = meta.aggregates.pairs?.map(p => {
		if (p[0] !== (order.sId ?? null) || p[1] !== (order.dId ?? null)) {
			return p;
		}
		const next = [...p];
		next[5] += dFee;
		next[9] = (next[9] || 0) + dPieces;
		return next;
	});
	return {
		items: nextItems,
		meta: {
			...meta,
			aggregates: {
				...meta.aggregates,
				totals: t,
				shops: adjust(meta.aggregates.shops, order.sId),
				deliveries: adjust(meta.aggregates.deliveries, order.dId),
				pairs,
			},
		},
	};
};

export const PageOrders = () => {
	const { user } = useStore();

	const isMasterOrAdmin = user?.role === 'admin' || user?.role === 'master';

	const [viewStateToShow, setViewStateToShow] = useState(VIEW_STATES.NORMAL);
	const [exporting, setExporting] = useState(false);
	const [orderRange, setOrderRange] = useState(null);

	const [deliveryOrders, setDeliveryOrders] = useState(null);
	const [shopOrders, setShopOrders] = useState(null);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState(null);

	// const [dateFrom, setDateFrom] = useState(DateTime.local().startOf('day').plus({ hours: 5 }));
	// const [dateTo, setDateTo] = useState(DateTime.local().startOf('day').plus({ days: 1, hours: 5 }));

	const customStart = getCustomStartOfDay();
	const [dateFrom, setDateFrom] = useState(customStart);
	const [dateTo, setDateTo] = useState(customStart.plus({ days: 1 }));

	// const [splitTimeFrom, setSplitTimeFrom] = useState(undefined);
	// const [splitTimeTo, setSplitTimeTo] = useState(undefined);

	// const [errorTimeFrom, setErrorTimeFrom] = useState(undefined);
	// const [errorTimeTo, setErrorTimeTo] = useState(undefined);

	const [allShops, setAllShops] = useState([]);
	const [allDeliveries, setAllDeliveries] = useState([]);

	const [selectedShops, setSelectedShops] = useState([]);
	const [selectedDeliveries, setSelectedDeliveries] = useState([]);
	const [selectedShopsNumber, setSelectedShopsNumber] = useState(0);
	const [selectedDeliveriesNumber, setSelectedDeliveriesNumber] = useState(0);

	const shopsRef = useRef();
	const deliveriesRef = useRef();

	const { data: dataShops } = useSWR(
		!!user?.cityLocation && isMasterOrAdmin ? SHOP_URL : false,
		() => ShopController.getAll(user?.cityLocation?.id),
		{ revalidateOnFocus: false, revalidateOnReconnect: false },
	);

	const { data: dataDeliveries } = useSWR(
		!!user?.cityLocation && isMasterOrAdmin ? DELIVERY_URL : false,
		() => DeliveryController.getAll(user?.cityLocation?.id),
		{ revalidateOnFocus: false, revalidateOnReconnect: false },
	);

	useEffect(() => {
		shopsRef?.current?.parentToggleModal(false);
		deliveriesRef?.current?.parentToggleModal(false);
	}, []);

	useEffect(() => {
		if (!dataShops?.shops) {
			return;
		}
		const shops = [
			...dataShops.shops.map(it => ({
				...it,
				label: it.name,
				value: it.id,
			})),
		].sort((a, b) => a.label.localeCompare(b.label, 'el'));
		setAllShops(shops);
		setSelectedShops(shops);
	}, [dataShops?.shops]);

	useEffect(() => {
		if (!dataDeliveries?.deliveries) {
			return;
		}
		const deliveries = [
			...dataDeliveries.deliveries.map(it => ({
				...it,
				label: `${it?.firstname} ${it?.lastname}`,
				value: it.id,
			})),
		].sort((a, b) => a.label.localeCompare(b.label, 'el'));
		setAllDeliveries(deliveries);
		setSelectedDeliveries(deliveries);
	}, [dataDeliveries?.deliveries]);

	// No memoization needed: useCursorPagination reads fetchPage through a ref.
	const fetchCompletedPage = async ({ args, cursor, dateField, limit }) => {
		const res = await OrderController.getCompleted(user?.cityLocation?.id, {
			...args,
			limit,
			cursor: cursor ?? undefined,
			dateField: dateField ?? undefined,
		});
		if (res?.error) {
			throw res;
		}
		return {
			items: res.completedOrders || [],
			hasMore: res.hasMore,
			nextCursor: res.nextCursor,
			dateField: res.dateField,
			// First page only — range-wide numbers for the summary panel.
			meta: cursor
				? undefined
				: {
						aggregates: res.aggregates,
						deliverygas: res.deliverygas,
						totalCount: res.totalCount,
					},
		};
	};

	const pager = useCursorPagination(fetchCompletedPage, { limit: PAGE_SIZE });

	useEffect(() => {
		if (pager.error) {
			toast.error('Κάποιο πρόβλημα προέκυψε');
		}
	}, [pager.error]);

	const fetchOrders = async (when = '') => {
		try {
			let obj;
			// if (when === 'today') {
			// 	obj = {
			// 		range: [
			// 			DateTime.local().startOf('day').toUTC().toISO(),
			// 			DateTime.local().endOf('day').toUTC().toISO(),
			// 		],
			// 	};
			// } else {
			// 	const valueFrom = getValues().timeFrom;
			// 	const valueTo = getValues().timeTo;

			// 	let from = dateFrom.startOf('day');

			// 	if (valueFrom) {
			// 		console.log('nai', valueFrom);
			// 	} else {
			// 		from = from.startOf('day');
			// 	}

			// 	from = from.toUTC().toISO();

			// 	let to = dateTo.startOf('day');

			// 	if (valueTo) {
			// 		// to = to
			// 		// .plus({
			// 		// 	hours: splitTimeTo?.hours || undefined,
			// 		// 	minutes: splitTimeTo?.minutes || undefined,
			// 		// });
			// 	} else {
			// 		to = to.endOf('day');
			// 	}

			// 	to = to.toUTC().toISO();

			// 	obj = { range: [from, to] };

			// 	// }
			// }

			if (when === 'today') {
				obj = {
					range: [
						DateTime.local().startOf('day').plus({ hours: 5 }),
						DateTime.local().startOf('day').plus({ days: 1, hours: 5 }),
						// DateTime.local().startOf('day').toUTC().toISO(),
						// DateTime.local().endOf('day').toUTC().toISO(),
					],
				};
			} else {
				obj = { range: [dateFrom, dateTo] };
			}
			const shopIds = [];
			const deliveryIds = [];

			selectedShops.forEach(it => {
				if (it.checked) {
					shopIds.push(Number(it.id));
				}
			});
			selectedDeliveries.forEach(it => {
				if (it.checked) {
					deliveryIds.push(Number(it.id));
				}
			});

			const data = {
				range: JSON.stringify(obj.range),
				shopIds:
					!shopIds?.length || selectedShopsNumber === allShops.length || !selectedShops?.length
						? undefined
						: JSON.stringify(shopIds),
				deliveryIds:
					!deliveryIds?.length ||
					selectedDeliveriesNumber === allDeliveries.length ||
					!selectedDeliveries?.length
						? undefined
						: JSON.stringify(deliveryIds),
			};

			// console.log('completed data:', data);

			// The args object is the query key: every «OK» press (and the initial
			// load) starts over from page 1 with these filters.
			const ok = await pager.reset(data);
			if (ok) {
				setViewStateToShow(VIEW_STATES.NORMAL);
			} else {
				console.log('ERROR getCompleted');
			}
		} catch (err) {
			console.log(err);
		}
	};

	const fetchOrdersRef = useRef(fetchOrders);

	useEffect(() => {
		// kano auto to karagioziliki giati petaei react compiler error
		fetchOrdersRef.current('today');
	}, []);

	const fetchOrderRange = async () => {
		try {
			let from = dateFrom.startOf('day');
			let to = dateTo.startOf('day');

			const daysObj = from.diff(to, 'days').toObject();
			// console.log(daysObj);
			const daysCountRounded =
				typeof daysObj.days === 'number' ? Math.round(daysObj.days * -1) + 1 : undefined;

			let range = [];
			// console.log("daysCountRounded", daysCountRounded);
			for (let i = 0; i < daysCountRounded; i++) {
				let day = from.plus({ days: i });
				let item = {
					from: day,
					// .plus({
					// 	hours: splitTimeFrom?.hours || undefined,
					// 	minutes: splitTimeFrom?.minutes || undefined,
					// })
					to: day,
					// .plus({
					// 	hours: splitTimeTo?.hours || 23,
					// 	minutes: splitTimeTo?.minutes || 59,
					// 	seconds: 59,
					// })
				};

				item.from = item.from.toUTC().toISO();
				item.to = item.to.toUTC().toISO();

				// TODO edo exo egglima, crime, einai terastio to query
				range.push(item);
			}

			// let obj;

			// if (when === 'today') {
			// 	obj = {
			// 		range: [
			// 			DateTime.local().startOf('day').plus({ hours: 5 }),
			// 			DateTime.local().startOf('day').plus({ days: 1, hours: 5 }),
			// 			// DateTime.local().startOf('day').toUTC().toISO(),
			// 			// DateTime.local().endOf('day').toUTC().toISO(),
			// 		],
			// 	};
			// } else {
			// 	obj = { range: [dateFrom, dateTo] };
			// }
			// obj = { range: [dateFrom, dateTo] };

			const shopIds = [];
			const deliveryIds = [];

			selectedShops.forEach(it => {
				if (it.checked) {
					shopIds.push(Number(it.id));
				}
			});
			selectedDeliveries.forEach(it => {
				if (it.checked) {
					deliveryIds.push(Number(it.id));
				}
			});

			const res = await OrderController.getOrderRange(user.cityLocation?.id, {
				// range: [...range],
				// range: JSON.stringify(range),
				range: JSON.stringify([dateFrom, dateTo]),
				// originalTimes: {
				// 	from: {
				// 		hours: splitTimeFrom?.hours || undefined,
				// 		minutes: splitTimeFrom?.minutes || undefined,
				// 	},
				// 	to: {
				// 		hours: splitTimeTo?.hours || undefined,
				// 		minutes: splitTimeTo?.minutes || undefined,
				// 	},
				// },
				offset: DateTime.local().offset,

				shopIds:
					!shopIds?.length || selectedShopsNumber === allShops.length || !selectedShops?.length
						? undefined
						: JSON.stringify(shopIds),
				deliveryIds:
					!deliveryIds?.length ||
					selectedDeliveriesNumber === allDeliveries.length ||
					!selectedDeliveries?.length
						? undefined
						: JSON.stringify(deliveryIds),
			});
			// console.log(res?.days);
			if (res?.days) {
				setViewStateToShow(VIEW_STATES.ORDER_RANGE);
				setOrderRange(res.days);
			} else {
				console.log('ERROR get order range', res);
			}
		} catch (err) {
			toast.error(
				`Κάποιο πρόβλημα προέκυψε${typeof err === 'string' ? ':' : ''}${
					typeof err === 'string' ? ` ${err}` : ''
				}`,
			);
			console.log(err);
		}
		// } else {
		//   console.log("not", range);
		// }
	};

	const openDeliveryOrders = orders => {
		setDeliveryOrders(orders);
	};

	const openShopOrders = orders => {
		setShopOrders(orders);
	};

	const add = (forArray, value) => {
		const newArr = [...(forArray === 'shops' ? selectedShops : selectedDeliveries)];
		for (let it of newArr) {
			it.checked = value;
		}

		if (forArray === 'shops') {
			setSelectedShops(newArr);
			setSelectedShopsNumber(value ? allShops.length : 0);
		} else {
			setSelectedDeliveries(newArr);
			setSelectedDeliveriesNumber(value ? allDeliveries.length : 0);
		}
	};

	const onShopSelect = idx => {
		// console.log("idxxx", selectedShops);
		// console.log("idxxxx", selectedShops[idx]);
		// console.log("wwww", shopId);
		setSelectedShops(s => {
			const newArr = [...s];
			newArr[idx].checked = !s[idx].checked;
			return newArr;
		});

		let num = 0;
		selectedShops.forEach(it => it.checked && num++);
		setSelectedShopsNumber(num);
		// if (shopId === "all") {
		//   setSelectedShop(allShops[0]);
		//   // console.log("zzz", allShops[0]);
		// } else {
		//   setSelectedShop(allShops.find((it) => it.id == shopId));
		// }
		// shopsRef?.current?.parentToggleModal(false, true);
	};

	const onDeliverySelect = idx => {
		// console.log("wwww", shopId);
		setSelectedDeliveries(s => {
			const newArr = [...s];
			newArr[idx].checked = !s[idx].checked;
			return newArr;
		});

		let num = 0;
		selectedDeliveries.forEach(it => it.checked && num++);
		setSelectedDeliveriesNumber(num);

		// if (deliveryId === "all") {
		//   setSelectedDelivery(allDeliveries[0]);
		//   // console.log("zzz", allShops[0]);
		// } else {
		//   setSelectedDelivery(allDeliveries.find((it) => it.id == deliveryId));
		// }
		// deliveriesRef?.current?.parentToggleModal(false, true);
	};

	// const onTimeChange = (value) => {
	//   console.log(value);
	//   console.log(value.toISO());
	//   setTime(value);
	// };

	const onDayChangeFrom = value => {
		setDateFrom(value);
	};

	const onDayChangeTo = value => {
		setDateTo(value);
	};

	// const trimStartAndEndValue = value => {
	// 	let v = '';
	// 	let idx = undefined;
	// 	for (let i = 0; i < value?.length; i++) {
	// 		if (value[i] !== ' ') {
	// 			idx = i;
	// 			break;
	// 		}
	// 	}
	// 	// console.log("idx ", idx);

	// 	if (idx !== undefined) {
	// 		for (let i = idx; i < value?.length; i++) {
	// 			v += value[i];
	// 		}
	// 	}

	// 	idx = undefined;
	// 	for (let i = v?.length - 1; i >= 0; i--) {
	// 		if (v[i] !== ' ') {
	// 			idx = i;
	// 			break;
	// 		}
	// 	}

	// 	const oldV = v;
	// 	v = '';

	// 	for (let i = 0; i <= idx; i++) {
	// 		v += oldV[i];
	// 	}

	// 	return v;
	// };

	const openOrder = useCallback(
		async (id, forOrderRange = false) => {
			if (!isMasterOrAdmin) {
				return;
			}

			if (forOrderRange) {
				for (let i = 0; i < orderRange.length; i++) {
					for (let j = 0; j < orderRange[i].hourRanges.length; j++) {
						for (let k = 0; k < orderRange[i].hourRanges[j].deliveriesInfo.length; k++) {
							for (
								let p = 0;
								p < orderRange[i].hourRanges[j].deliveriesInfo[k].orders.length;
								p++
							) {
								if (orderRange[i].hourRanges[j].deliveriesInfo[k].orders[p].id === id) {
									setSelectedOrder(orderRange[i].hourRanges[j].deliveriesInfo[k].orders[p]);
									setIsModalOpen(true);
									return;
								}
							}
						}
					}
				}
			} else {
				setSelectedOrder(pager.items?.find(it => it.id == id));
				setIsModalOpen(true);
			}
		},
		[orderRange, pager.items],
	); // match actual deps

	// Stable reference: the Table memo compares onClick by identity, so an inline
	// arrow would re-render every loaded row on each keystroke of this page.
	const onRowClick = useCallback(id => openOrder(id, false), [openOrder]);

	const onCloseModal = () => {
		setIsModalOpen(false);
		setSelectedOrder(null);
	};

	const showFees = user.cityLocation?.withFees && isMasterOrAdmin;
	// the shop-billing fee is also the shop's own bill, so shops see it too —
	// unless the city hid it from them (hideShopFeesFromShops)
	const showShopFees = canSeeShopFees(user);
	// Same rule as the Χρόνος column: a shop that may not see per-order times
	// must not see their average either.
	const showTime = user?.role !== 'shop' || !user?.cityLocation?.withHideHistoryTime;

	// Piece work: a «Τεμάχια» column only when some loaded order has pieces.
	const hasPieces = !!pager.items?.some(it => it.pcs !== undefined && it.pcs !== null);

	const columns = React.useMemo(
		() => [
			{
				header: '#',
				accessorKey: 'index',
			},
			// {
			// 	header: '#',
			// 	accessorKey: 'id',
			// },
			// {
			// 	header: '#',
			// 	accessorKey: 'tempDate',
			// },
			// {
			// 	header: '#',
			// 	accessorKey: 'originalOrderCreatedAtTemp',
			// },

			...(isMasterOrAdmin
				? [
						{
							header: 'Κατάστημα',
							accessorKey: 'shopName',
						},
					]
				: []),
			{
				header: 'Διεύθυνση',
				accessorKey: 'address',
			},
			// The area column belongs to cities that actually use areas. It used to
			// be gated on withHideHistoryTime, which is about hiding the time.
			...(user?.cityLocation?.areas?.length > 0
				? [
						{
							header: 'Περιοχή',
							accessorKey: 'area',
						},
					]
				: []),
			{
				header: 'Διανομέας',
				accessorKey: 'deliveryName',
			},
			{
				header: 'Πελάτης',
				accessorKey: 'clientName',
			},
			{
				header: 'Σχόλια',
				accessorKey: 'comments',
			},
			{
				header: 'Τιμή',
				accessorKey: 'price',
				cell: ({ row }) => (
					// row.original.pr ||
					<div className='min-w-18'>
						{row.original?.price}
						{row.original.isCard ? ' (Κ)' : ''}
					</div>
				),
			},
			// {
			// 	header: 'Κάρτα',
			// 	accessorKey: 'isCard',
			// },

			// showFees is already admin/master only — withHideHistoryTime never had
			// a say over the fee, only over the time column below.
			...(showFees
				? [
						{
							header: 'Προμήθεια',
							accessorKey: 'fee',
						},
					]
				: []),
			...(showShopFees
				? [
						{
							header: 'Χρέωση',
							accessorKey: 'shopFee',
						},
					]
				: []),
			...(hasPieces
				? [
						{
							header: 'Τεμάχια',
							accessorKey: 'pieces',
						},
					]
				: []),
			...(user?.role !== 'shop' || !user?.cityLocation?.withHideHistoryTime
				? [
						{
							header: 'Χρόνος',
							accessorKey: 'thirdTime',
						},
					]
				: []),
			...(user?.role !== 'shop'
				? [
						{
							header: 'Χλμ.',
							accessorKey: 'distanceMeters',
						},
					]
				: []),
			// Straight-line shop -> client distance, stored on the order at creation
			// time. Nothing to gate it on: it costs nothing, it is not money, and an
			// order without coordinates just shows "-".
			{
				header: 'Ακτίνα',
				accessorKey: 'radiusMeters',
			},

			{
				header: 'Πλατφόρμα',
				accessorKey: 'platform',
				// cell: ({ row }) => row.original.platform && <PlatformLogo platform={row.original.platform} />,
			},
		],
		[hasPieces],
	);

	const dataTable = React.useMemo(
		() => pager.items?.map((it, index) => mapOrderRow(it, index, isMasterOrAdmin)) ?? [],
		[pager.items, isMasterOrAdmin],
	);

	const deliveryOrdersTable = React.useMemo(
		() =>
			deliveryOrders?.map((it, index) => ({
				id: it.id,
				index: index + 1,
				thirdTime: `${it?.tt ? `${it.tt}'` : ''}`,
				// createdAt: `${DateTime.fromISO(it.createdAt).toFormat(
				//   "dd/LL/yyyy - HH:mm"
				// )} ${it?.thirdTime ? `(${it.thirdTime})'` : ""}`, //=> 'April 20',
				price: it?.pr || '-',
				comments: it?.c || '',
				deliveryName: it.d?.name || '',
				shopName: isMasterOrAdmin ? `${it.sh?.name}` : undefined,
				clientName: `${it.cl?.f || ''}${it.cl?.p || it.cPh ? ` (${it.cl?.p || it.cPh})` : ''}`,
				address: it.a?.s || it?.area,
			})),
		[deliveryOrders],
	);

	const shopOrdersTable = React.useMemo(
		() =>
			shopOrders?.map((it, index) => ({
				id: it.id,
				index: index + 1,
				thirdTime: `${it?.tt ? `${it.tt}'` : ''}`,
				// createdAt: `${DateTime.fromISO(it.createdAt).toFormat(
				//   "dd/LL/yyyy - HH:mm"
				// )} ${it?.thirdTime ? `(${it.thirdTime})'` : ""}`, //=> 'April 20',
				price: it?.pr || '-',
				comments: it?.c || '',
				deliveryName: it.d?.name || '',
				shopName: isMasterOrAdmin ? `${it.sh?.name}` : undefined,
				clientName: `${it.cl?.f || ''}${it.cl?.p || it.cPh ? ` (${it.cl?.p || it.cPh})` : ''}`,
				address: it.a?.s || it?.area,
			})),
		[shopOrders],
	);

	const onDeleteOrder = async orderId => {
		try {
			const res = await OrderController.delete(orderId);
			if (!res?.error) {
				// Local removal instead of a refetch: keeps the loaded pages and the
				// scroll position, and adjusts the server aggregates by the known row.
				pager.mutate(state => removeOrderLocally(state, orderId));
				onCloseModal();
				toast.success('Η παραγγελία διαγράφτηκε');
				return;
			}
			toast.error(
				`Κάποιο πρόβλημα προέκυψε${typeof res.error === 'string' || res?.code ? ':' : ''}${
					typeof res.error === 'string' ? ` ${res.error}` : ''
				}${res?.code ? ` (${res.code})` : ''}`,
			);
		} catch (err) {
			toast.error(
				`Κάποιο πρόβλημα προέκυψε${typeof err.error === 'string' || err?.code ? ':' : ''}${
					typeof err.error === 'string' ? ` ${err.error}` : ''
				}${err?.code ? ` (${err.code})` : ''}`,
			);
			console.log('onDeleteOrder err:', err);
		}
	};

	const excel = async () => {
		if (viewStateToShow !== VIEW_STATES.NORMAL || exporting) {
			return;
		}
		setExporting(true);
		try {
			// The whole date range, not only the loaded pages — walk the cursor to
			// the end without pushing the extra rows into the table.
			const allOrders = await pager.fetchRest({ limit: 500 });
			const rows = allOrders
				.map((o, index) => mapOrderRow(o, index, isMasterOrAdmin))
				.map(it => ({
					ID: it.id,
					Ημερομηνία: it.createdAtDate,
					Κατάστημα: it.shopName,
					Διεύθυνση: it.address,
					// ...(user?.cityLocation?.withHideHistoryTime ? { Περιοχή: it.area } : {}),
					Περιοχή: it.area ?? '',
					Διανομέας: it.deliveryName,
					Πελάτης: it.clientName,
					Τιμή: it.price,
					Πληρωμή: it.isCard ? 'Κάρτα' : 'Μετρητά',
					// same visibility as the table columns: the rider fee is
					// admin-only, the shop charge also goes to the shop itself
					...(showFees ? { Προμήθεια: it.fee } : {}),
					...(showShopFees ? { Χρέωση: it.shopFee } : {}),
					// distanceMeters/radiusMeters are already km strings ('3.4' or '-')
					Χιλιόμετρα: it.distanceMeters === '-' ? '' : Number(it.distanceMeters),
					...(showTime ? { Χρόνος: it.thirdTime } : {}),
					Ακτίνα: it.radiusMeters === '-' ? '' : Number(it.radiusMeters),
					Πλατφόρμα: it.platform ?? '',
				}));

			// totals over the exported rows — the whole range, thanks to fetchRest
			const sum = key =>
				parseFloat(rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0).toFixed(2));

			if (rows.length) {
				rows.push({
					ID: 'ΣΥΝΟΛΟ',
					Πελάτης: `${rows.length} παραγγελίες`,
					Τιμή: sum('Τιμή'),
					...(showFees ? { Προμήθεια: sum('Προμήθεια') } : {}),
					...(showShopFees ? { Χρέωση: sum('Χρέωση') } : {}),
					Χιλιόμετρα: sum('Χιλιόμετρα'),
					Ακτίνα: sum('Ακτίνα'),
				});
			}

			exportToExcel(rows, 'orders.xlsx');
		} catch (err) {
			toast.error('Κάποιο πρόβλημα προέκυψε');
			console.log('excel err: ', err);
		} finally {
			setExporting(false);
		}
	};

	const date = new Date(selectedOrder?.originalOrderCreatedAt || selectedOrder?.createdAt);

	let displayFrom = date;
	let yyyy = displayFrom.getFullYear();
	let mm = displayFrom.getMonth() + 1;
	let dd = displayFrom.getDate();

	if (dd < 10) dd = '0' + dd;
	if (mm < 10) mm = '0' + mm;

	displayFrom =
		dd +
		'/' +
		mm +
		'/' +
		yyyy +
		' - ' +
		date.getHours() +
		':' +
		`${date.getMinutes() < 10 ? '0' : ''}${date.getMinutes()}`;

	return (
		<div className={s.container}>
			<div className={s.calendarContainer}>
				{/* <RangePicker
					dateFrom={dateFrom}
					dateTo={dateTo}
					onDayChangeFrom={onDayChangeFrom}
					onDayChangeTo={onDayChangeTo}
					maxDate={maxDate}
					control={control}
					withTime
					disablePast={user.id == 7818}
				/> */}

				<div className='flex flex-row gap-x-2 sm:gap-x-4'>
					<MyDateTimePicker
						label='Από'
						value={dateFrom}
						onChange={onDayChangeFrom}
						{...(user?.hidePastOrders
							? {
									minDate: getCustomStartOfDay().minus({ days: 1 }),
								}
							: {})}
						maxDate={DateTime.local()}
						format='dd/MM/yy HH:mm'
					/>

					<MyDateTimePicker
						label='Μέχρι'
						value={dateTo}
						onChange={onDayChangeTo}
						maxDate={maxDate}
						format='dd/MM/yy HH:mm'
					/>
				</div>
			</div>

			<div className='flex flex-row gap-y-10 sm:flex-row'>
				{isMasterOrAdmin && (
					<div className='flex flex-col sm:flex-row'>
						<div className={s.choiceContainer}>
							<div className={s.choiceTopContainer}>
								<div className={s.dropdownLabel}>{selectedShop?.label}</div>
								<div
									className={s.dropdownButton}
									onClick={() => shopsRef.current.parentToggleModal(true, true)}
								>
									{selectedShopsNumber === 0 || selectedShopsNumber === allShops.length
										? 'Όλα'
										: `${selectedShopsNumber} επιλεγμέν${selectedShopsNumber === 1 ? 'ο' : 'α'}`}
								</div>
							</div>

							<div style={{ position: 'relative' }}>
								<OutsideClickHandler
									onOutsideClick={() => shopsRef.current.parentToggleModal(false)}
								>
									<DropdownMultiple
										forArray='shops'
										ref={shopsRef}
										onChange={onShopSelect}
										options={selectedShops}
										add={add}
									/>
								</OutsideClickHandler>
							</div>
						</div>

						<div className={s.choiceContainer}>
							<div className={s.choiceTopContainer}>
								<div className={s.dropdownLabel}>{selectedDelivery?.label}</div>
								<div
									className={s.dropdownButton}
									onClick={() => deliveriesRef.current.parentToggleModal(true, true)}
								>
									{selectedDeliveriesNumber === 0 ||
									selectedDeliveriesNumber === allDeliveries.length
										? 'Όλοι'
										: `${selectedDeliveriesNumber} επιλεγμέν${
												selectedDeliveriesNumber === 1 ? 'ος' : 'οι'
											}`}
								</div>
							</div>

							<div style={{ position: 'relative', width: 208 }}>
								<OutsideClickHandler
									onOutsideClick={() => deliveriesRef.current.parentToggleModal(false)}
								>
									<DropdownMultiple
										forArray='deliveries'
										ref={deliveriesRef}
										onChange={onDeliverySelect}
										options={selectedDeliveries}
										add={add}
									/>
								</OutsideClickHandler>
							</div>
						</div>
					</div>
				)}

				<div className='flex flex-col gap-4 sm:flex-row'>
					<ButtonSecondary
						text='Σήμερα'
						onClick={() => {
							// fetchOrders('today')
							setDateFrom(DateTime.local().startOf('day').plus({ hours: 5 }));
							setDateTo(DateTime.local().startOf('day').plus({ days: 1, hours: 5 }));
						}}
						styleStyle={{ width: 100, padding: '6px 4px 6px 4px' }}

						// extraStyle={s.addButton}
					/>
					{isMasterOrAdmin && (
						<ButtonSecondary
							onClick={excel}
							text={exporting ? 'Λήψη…' : 'Excel'}
							styleStyle={{ width: 100, padding: '6px 4px 6px 4px' }}
						/>
					)}
				</div>
			</div>

			<div className='flex flex-col gap-2 min-[861px]:flex-row'>
				<div className={s.buttonsContainer}>
					<Button text='OK' onClick={fetchOrders} extraStyle={s.addButton} />
					{isMasterOrAdmin && (
						<Button text='Αναλυτικά' onClick={fetchOrderRange} extraStyle={s.addButton} />
					)}
				</div>
			</div>

			{viewStateToShow === VIEW_STATES.NORMAL
				? pager.items?.length > 0 && (
						<>
							<OrderSummary
								orders={pager.items}
								aggregates={pager.meta?.aggregates}
								deliverygas={pager.meta?.deliverygas}
								showFees={showFees}
								showShopFees={showShopFees}
								showTime={showTime}
								isMasterOrAdmin={isMasterOrAdmin}
							/>
							<div className='mt-4'>
								<Table
									columns={columns}
									data={dataTable}
									onClick={onRowClick}
									onEndReached={pager.loadMore}
									isLoadingMore={pager.isLoadingMore}
								/>
							</div>
							{pager.hasMore && (
								<div className='mt-2 text-sm text-slate-500'>
									{pager.items.length}
									{typeof pager.meta?.totalCount === 'number'
										? ` από ${pager.meta.totalCount}`
										: ''}
								</div>
							)}
						</>
					)
				: viewStateToShow === VIEW_STATES.ORDER_RANGE
					? orderRange?.length > 0 && (
							<>
								<div className={s.title_label}>Διανομείς</div>
								{orderRange.map(dateData => (
									<DayView
										key={`${dateData.day}${dateData.month}`}
										dateData={dateData}
										openOrders={openDeliveryOrders}
										field='deliveriesInfo'
										color='rgb(187, 217, 219)'
									/>
								))}
								{deliveryOrders?.length > 0 && deliveryOrdersTable && (
									<div style={{ marginTop: 20 }}>
										<Table
											columns={columns}
											data={deliveryOrdersTable}
											onClick={id => openOrder(id, true)}
										/>
									</div>
								)}

								<div className={s.title_label}>Καταστήματα</div>
								{orderRange.map(dateData => (
									<DayView
										key={`${dateData.day}${dateData.month}`}
										dateData={dateData}
										openOrders={openShopOrders}
										field='shopsInfo'
										color='rgb(238, 201, 187)'
									/>
								))}
								{shopOrders?.length > 0 && shopOrdersTable && (
									<div style={{ marginTop: 20 }}>
										<Table
											columns={columns}
											data={shopOrdersTable}
											onClick={id => openOrder(id, true)}
										/>
									</div>
								)}
							</>
						)
					: null}

			{/* {isModalOpen && (
				<Modal onClose={onCloseModal}>
					<Order
						order={selectedOrder}
						onDeleteOrder={() => onDeleteOrder(selectedOrder.id)}
						withFees={isMasterOrAdmin && user?.cityLocation?.withHideHistoryTime}
					/>
				</Modal>
			)} */}
			<Pocket
				open={isModalOpen}
				// title={`${shopToAssignDelivery?.name}`}
				title={displayFrom || ''}
				onOpenChange={onCloseModal}
				onClose={() => onCloseModal(false)}
			>
				<Order
					order={selectedOrder}
					onDeleteOrder={() => onDeleteOrder(selectedOrder.id)}
					withFees={isMasterOrAdmin && user?.cityLocation?.withFees}
					withShopFees={isMasterOrAdmin && showShopFees}
					onPiecesChanged={(pieces, shopFee) => {
						pager.mutate(state => changePiecesLocally(state, selectedOrder.id, pieces, shopFee));
						setSelectedOrder(o => (o ? { ...o, pcs: pieces, shopFee } : o));
					}}
				/>
			</Pocket>
		</div>
	);
};
